import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { helpPageStyles } from "../../core/styles/cards/helpPageStyles";
import {
  SUPPORT_EMAIL,
  SUPPORT_CATEGORIES,
  submitViaEmail,
  getCurrentUser,
  userDisplayName,
  userEmail,
} from "../configs/helpConfig";

/**
 * Support request form. Collects a category, subject, and description and sends
 * a structured message to the support team. The signed-in user's name, email,
 * and school are attached automatically so support has context.
 */
const SupportForm = () => {
  const theme = useTheme();
  const styles = helpPageStyles(theme);
  const user = getCurrentUser();

  const [category, setCategory] = useState(SUPPORT_CATEGORIES[0]);
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [snack, setSnack] = useState(false);

  const canSubmit = subject.trim() && description.trim();

  const handleSubmit = () => {
    if (!canSubmit) return;
    const reporter = userDisplayName(user);
    const email = userEmail(user);
    const body =
      `Category: ${category}\n` +
      `From: ${reporter || "(name not set)"}${email ? ` <${email}>` : ""}\n` +
      (user.school ? `School/District: ${user.school}\n` : "") +
      `\n${description}\n`;
    submitViaEmail(`[Support] ${subject}`, body);
    setSnack(true);
    setSubject("");
    setDescription("");
  };

  return (
    <Box sx={styles.mdFormCard}>
      <Typography sx={styles.mdFormTitle}>Submit a support request</Typography>
      <Typography sx={styles.mdFormSubtitle}>
        Tell us what's going on and we'll follow up. Your name, email, and school
        are included automatically.
      </Typography>

      <TextField
        select
        fullWidth
        size="small"
        label="Category"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        sx={styles.mdFormField}
      >
        {SUPPORT_CATEGORIES.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        fullWidth
        size="small"
        label="Subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        sx={styles.mdFormField}
      />

      <TextField
        fullWidth
        multiline
        minRows={4}
        label="Describe your issue or question"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={styles.mdFormField}
      />

      <Button
        variant="contained"
        disabled={!canSubmit}
        onClick={handleSubmit}
        sx={styles.mdFormButton}
      >
        Send to support
      </Button>

      <Snackbar
        open={snack}
        autoHideDuration={6000}
        onClose={() => setSnack(false)}
      >
        <Alert severity="success" onClose={() => setSnack(false)} sx={{ width: "100%" }}>
          Opening your email app to send this to our support team. If nothing
          opened, email {SUPPORT_EMAIL}.
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportForm;
